<template>
  <form @submit.prevent>
    <div>
      <b-tabs v-if="showLibraryItems" justified nav-class="nav-tabs-custom" content-class="p-3 text-muted">
        <b-tab :active="showLibraryItems">
          <template v-slot:title>
            <span class="d-inline-block d-sm-none">
              <i class="fas fa-home"></i>
            </span>
            <span class="d-none d-sm-inline-block">{{ $t("common.library") }}</span>
          </template>

          <LibraryItems
            rightPanelId="sidebar-new-datasubject"
            organizationScopedState="organizationDataDatasubject"
            libraryScopedState="datasubject"
            @interceptForSetParent="interceptForSetParent"
            libraryScopedStateCategory="datasubjectCategory"
          />
        </b-tab>

        <b-tab :active="isEditMode">
          <template v-slot:title>
            <span class="d-inline-block d-sm-none">
              <i class="far fa-user"></i>
            </span>
            <span class="d-none d-sm-inline-block">
              <span v-if="isEditMode">{{ $t("common.update") }}</span>
              <span v-else>{{ $t("datasubject.new") }}</span>
            </span>
          </template>

          <sweet-input
            :isValid="!$v.form.name.$error"
            @input="$v.form.name.$touch()"
            v-model="form.name"
            type="text"
            layout="vertical"
            :placeholder="$t('datasubject.name')"
            :label="$t('datasubject.name')"
          />

          <div class="col-mr-5 mt-3">
            <right-panel
              class="float-right mt-1 "
              id="sidebar-new-datasubject-category"
              width="650px"
              :icon="'mdi mdi-plus'"
              :text="$t('datasubject-category.new')"
              @hidden="rightPanelClosed"
            >
              <div class="pl-5 pr-5">
                <Form :hide-library="true" />
              </div>
            </right-panel>
          </div>

          <h6 class="mt-4">{{ $t("datasubject-category.text") }}</h6>
          <filter-select
            id="categoryFilter"
            ref="categoryFilter"
            :placeholder="$t('datasubject-category.text')"
            :multiple="false"
            @filter="onCategorySearch"
            :selected="form.category"
            @changed="onCategorySelect"
          />

          <h6 class="mt-4">{{ $t("data.text") }}</h6>
          <filter-select id="dataFilter" ref="dataFilter" :placeholder="$t('data.text')" :multiple="false" @filter="onSearch" :selected="form.data" @changed="onSelect" />

          <sweet-button
            @click="save()"
            type="submit"
            :loading="isUpdating"
            :disabled="isUpdating || $v.$invalid"
            variant="success"
            icon="mdi mdi-check"
            size="sm"
            buttonClass="mt-2"
          >
            {{ $t("common.save") }}
          </sweet-button>
        </b-tab>
      </b-tabs>

      <div v-else>
        <h2>
          <span v-if="!showLibraryItems && isEditMode">{{ entityName }} {{ $t("common.update") }}</span>
          <span v-else>{{ $t("datasubject.new") }}</span>
        </h2>

        <sweet-input
          :isValid="!$v.form.name.$error"
          @input="$v.form.name.$touch()"
          v-model="form.name"
          type="text"
          layout="vertical"
          :placeholder="$t('datasubject.name')"
          :label="$t('datasubject.name')"
        />

        <h6 class="mt-4">{{ $t("datasubject-category.text") }}</h6>
        <filter-select
          v-if="form.category"
          id="categoryFilter"
          ref="categoryFilter"
          :placeholder="$t('datasubject-category.text')"
          :multiple="false"
          @filter="onCategorySearch"
          :selected="form.category"
          @changed="onCategorySelect"
        />
        <filter-select
          v-else
          id="categoryFilter"
          ref="categoryFilter"
          :placeholder="$t('datasubject-category.text')"
          :multiple="false"
          @filter="onCategorySearch"
          :selected="form.datasubject.category"
          @changed="onCategorySelect"
        />

        <h6 class="mt-4">{{ $t("data.text") }}</h6>
        <filter-select id="dataFilter1" ref="dataFilter1" :placeholder="$t('data.text')" :multiple="false" @filter="onSearch" :selected="form.data" @changed="onSelect" />

        <div v-if="form && form._id" class="mt-4">
          <b-form-checkbox v-model="changeInAllHierarchy" name="check-button" switch class="switch d-inline"></b-form-checkbox>
          <label class="switch">{{ $t("common.change-in-all-hierarchy") }}</label>
        </div>

        <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
          {{ $t("common.save") }}
        </sweet-button>

        <sweet-button @click="cancel()" variant="secondary" icon="mdi mdi-cancel" size="sm" buttonClass="mt-2 ml-2">
          {{ $t("common.cancel") }}
        </sweet-button>
      </div>
    </div>
  </form>
</template>

<script>
import LibraryItems from "@cc/library-items";

import { maxLength, minLength, required } from "vuelidate/lib/validators";
import Form from "../datasubject/organization-datasubject-category-form.vue";
export default {
  props: ["hide-library"],
  components: {
    LibraryItems,
    Form,
  },
  data() {
    return {
      form: { name: undefined },
      changeInAllHierarchy: false,
      formError: undefined,
      entityName: undefined,
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
    },
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = { ...this.prepareFormForEdit(v) };
        this.entityName = this.form.name;
      },
    },
    copingEntity: {
      deep: true,
      handler(v) {
        this.form = { ...this.prepareFormForEdit(v) };
        this.entityName = this.form.name;
      },
    },
    selectedDatasourceData: {
      deep: true,
      immediate: true,
      handler(v) {
        this.onSelect(v);
      },
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters["organizationDataDatasubject/isUpdating"];
    },
    editedEntity() {
      return this.$store.getters["organizationDataDatasubject/editedEntity"];
    },
    copingEntity() {
      return this.$store.getters["organizationDataDatasubject/copingEntity"];
    },
    isEditMode() {
      return this.$store.getters["organizationDataDatasubject/isEditMode"];
    },
    showLibraryItems() {
      return !this.hideLibrary && !this.isEditMode && !this.isCoping;
    },
    isCoping() {
      return this.$store.getters["organizationDataDatasubject/isCoping"];
    },
    selectedDatasourceData() {
      return this.$store.getters["organizationDatasourceData/selectedEntity"];
    },
  },
  mounted() {},
  methods: {
    rightPanelClosed() {
      this.$store.commit(`organizationDatasubjectCategory/editedEntity`, null);
      this.$refs?.categoryFilter?.searchChanged("*");
    },
    customLabel(value) {
      return this.$options.filters.translate(value.name);
    },
    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        await this.$store.dispatch("organizationDataDatasubject/save", { payload: this.form, changeInAllHierarchy: this.changeInAllHierarchy });
        this.form = { data: this.form.data };
        this.$toast.success(this.$t("common.success"));
        // this.$root.$emit("bv::toggle::collapse", "sidebar-new-datasubject");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    cancel() {
      this.$store.dispatch("organizationDataDatasubject/resetForm");
    },
    async onSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDatasourceData/paginateForSearch`, {
        options: { limit: 20 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onSelect(item) {
      this.form = { ...this.form, data: item };
    },

    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`datasubjectCategory/paginate`, {
        options: { limit: 20 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onCategorySelect(item) {
      this.form = { ...this.form, category: item };
    },

    interceptForSetParent(payload) {
      payload.data = this.selectedDatasourceData;
    },
  },
};
</script>
